// Supabase Client für Dashboard

import { createClient } from '@supabase/supabase-js';
import { processLock } from '@supabase/auth-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Supabase URL und Anon Key müssen in den Environment Variables gesetzt sein!\n' +
    'Erstellen Sie eine .env Datei mit:\n' +
    'VITE_SUPABASE_URL=your_supabase_url\n' +
    'VITE_SUPABASE_ANON_KEY=your_supabase_anon_key'
  );
}

// Custom fetch MIT Timeout. Grund: supabase-js bricht von sich aus keinen
// Request ab. Wenn der interne Token-Refresh (oder ein DB-Aufruf) im Netz
// stehen bleibt — z. B. nach Tab-Inaktivität / instabiler Verbindung —, hängt
// er UNBEGRENZT. Und weil der Refresh dabei den Auth-Lock hält, hängt danach
// JEDER weitere Aufruf mit (Login, „Speichern lädt ewig"). Mit AbortController
// bricht jeder Request nach 20 s ab → das Promise rejectet, der Button hört auf
// zu laden, der Lock wird frei und der nächste Versuch geht durch.
// 20 s ist bewusst großzügig, damit legitime Bild-Uploads (ImageUpload.tsx)
// nicht abgeschnitten werden.
const FETCH_TIMEOUT_MS = 20_000;
const fetchWithTimeout: typeof fetch = (input, init) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  // Ein vom Aufrufer mitgegebenes Abort-Signal weiterhin respektieren.
  const caller = init?.signal;
  if (caller) {
    if (caller.aborted) controller.abort();
    else caller.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return fetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
};

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Explizit localStorage (statt evtl. IndexedDB-Fallback in manchen WebKit-Builds).
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    // WICHTIG: Der Default-Lock ist `navigatorLock` (Web Locks API). Der ist
    // tab-übergreifend und blockiert regelmäßig Sekunden bis Minuten, wenn ein
    // Zombie-Tab, ein Service-Worker oder eine abgestürzte Vorgänger-Session den
    // Lock nicht freigibt. Folge: `getSession()`/`signInWithPassword()` UND jeder
    // authentifizierte DB-Aufruf (der auf den Token wartet) hängen → „Login lädt
    // ewig", „Speichern lädt ewig". `processLock` ist der in-memory-Lock (tab-
    // intern) und behebt den Deadlock. (Identisch zu PUBLIVO/content-system.)
    lock: processLock,
  },
  // Greift für ALLE Requests des Clients (Auth-Refresh, DB, Storage) → nichts
  // kann die UI mehr unbegrenzt einfrieren.
  global: { fetch: fetchWithTimeout },
});
